// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the Highlight colour, chosen in Appearance and kept per device. Ink is the
// default (no class); the presets put `highlight-<name>` on <html> for highlight.css;
// Custom writes the chosen colour and a readable text colour straight onto <html>.

import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import {autorun, makeAutoObservable, runInAction} from 'mobx';

export const HighlightColors = {
	INK: 'ink',
	VIOLET: 'violet',
	BLUE: 'blue',
	TEAL: 'teal',
	GREY: 'grey',
	ORANGE: 'orange',
	CUSTOM: 'custom',
} as const;
export type HighlightColor = (typeof HighlightColors)[keyof typeof HighlightColors];

const HIGHLIGHT_VALUES = new Set<string>(Object.values(HighlightColors));
const CLASS_PREFIX = 'highlight-';
const HEX_COLOR = /^#[0-9a-f]{6}$/i;
export const DEFAULT_CUSTOM_HIGHLIGHT = '#d9467a';

/** Near-black or white, whichever reads better on the colour (WCAG relative luminance). */
export function highlightContrast(hex: string): string {
	const channel = (index: number) => {
		const value = Number.parseInt(hex.slice(1 + index * 2, 3 + index * 2), 16) / 255;
		return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
	};
	const luminance = 0.2126 * channel(0) + 0.7152 * channel(1) + 0.0722 * channel(2);
	return luminance > 0.4 ? '#151518' : '#ffffff';
}

class HighlightPreferenceStore {
	color: HighlightColor = HighlightColors.INK;
	customColor: string = DEFAULT_CUSTOM_HIGHLIGHT;
	private _hydrated = false;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		void this.initPersistence();
		if (typeof document !== 'undefined') {
			autorun(() => {
				const classList = document.documentElement.classList;
				for (const name of Array.from(classList)) {
					if (name.startsWith(CLASS_PREFIX)) classList.remove(name);
				}
				const style = document.documentElement.style;
				if (this.color === HighlightColors.CUSTOM) {
					style.setProperty('--highlight', this.customColor);
					style.setProperty('--highlight-contrast', highlightContrast(this.customColor));
				} else {
					style.removeProperty('--highlight');
					style.removeProperty('--highlight-contrast');
					if (this.color !== HighlightColors.INK) classList.add(`${CLASS_PREFIX}${this.color}`);
				}
			});
		}
	}

	get hydrated(): boolean {
		return this._hydrated;
	}

	setColor(color: HighlightColor): void {
		if (HIGHLIGHT_VALUES.has(color)) this.color = color;
	}

	/** Pick a custom colour (#rrggbb) and switch to it. */
	setCustomColor(hex: string): void {
		if (!HEX_COLOR.test(hex)) return;
		this.customColor = hex.toLowerCase();
		this.color = HighlightColors.CUSTOM;
	}

	private async initPersistence(): Promise<void> {
		await makePersistent(this, 'HighlightPreference', ['color', 'customColor'], {syncAcrossTabs: true});
		runInAction(() => {
			if (!HIGHLIGHT_VALUES.has(this.color)) this.color = HighlightColors.INK;
			if (!HEX_COLOR.test(this.customColor)) this.customColor = DEFAULT_CUSTOM_HIGHLIGHT;
			this._hydrated = true;
		});
	}
}

export default new HighlightPreferenceStore();
